import React, { useEffect, useState } from "react";
import { Skeleton, Box, Grid, Typography } from "@mui/material";
import { BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import StyledPaper from "../common/styled-paper.component";
import DateRangePickerComponent from "../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from '../../hooks/useDashboard';
import EmptyIcon from "../../assets/images/empty-box.png";

const generateRandomColor = () => {
    // Generate a random hex color
    const randomColor = `#${Math.floor(Math.random() * 0xFFFFFF).toString(16)}`;
    return randomColor.padEnd(7, '0'); // Ensure it's always 7 characters (including '#')
};
  
const isColorDark = (color) => {
    // Calculate luminance of the color
    const rgb = parseInt(color.slice(1), 16); // Convert hex to RGB
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >>  8) & 0xff;
    const b = (rgb >>  0) & 0xff;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance < 0.5;
};

const generateRandomDarkColor = () => {
    // Generate a random hex color
    const randomColor = generateRandomColor();
    if(!isColorDark(randomColor)) generateRandomDarkColor();
    return randomColor;
};


const PeakVerificationTimeAnalysis = (props) => {
    const { totalVerificationPerHour, isFetchPeakVerificationTimeAnalysis, getTotalVerificationPerHour } = useDashboard();
    const [date_range, setDateRange] = useState([moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')]);
    const [formattedVerificationPerHour, setFormattedVerificationPerHour] = useState(null);
    const [countToColorMap, setCountToColorMap] = useState({}); // To map counts to unique colors

    // Fetch data when date range changes
    useEffect(() => {
        if (date_range == null) return;
        getTotalVerificationPerHour(date_range[0], date_range[1]);
    }, [date_range]);

    // Format the data once it is fetched
    useEffect(() => {
        if (totalVerificationPerHour == null) return;

        // Create a map of unique colors for each verification count
        const newCountToColorMap = {};
        const formatted_data = totalVerificationPerHour.map((verification_obj, i) => {
            const verification_count = verification_obj.count;
            const verification_hour = verification_obj.hour;
            const name = verification_hour < 10 ? `0${verification_hour}:00` : `${verification_hour}:00`;

            // Generate a unique color for each count
            if (!newCountToColorMap[verification_count]) {
                newCountToColorMap[verification_count] = generateRandomDarkColor(); // Assign a new random color
            }

            return { name, count: verification_count, color: newCountToColorMap[verification_count] };
        });

        setCountToColorMap(newCountToColorMap);
        setFormattedVerificationPerHour(formatted_data);
    }, [totalVerificationPerHour]);

    // Handle date range change
    const handleDateRangeChange = (startDate, endDate) => {
        setDateRange([startDate, endDate]);
    };

    return (
        <StyledPaper>
            <Grid container sx={{ pb: 2 }}>
                <Grid item xs={12} sm={6} lg={7.5}>
                    <Typography>Peak Verification Time Analysis</Typography>
                </Grid>
                <Grid item xs={12} sm={6} lg={4.5}>
                    <DateRangePickerComponent
                        initialStartDate={date_range[0]}
                        initialEndDate={date_range[1]}
                        ranges={{
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>

            {isFetchPeakVerificationTimeAnalysis ? (
                <Skeleton variant="rounded" sx={{ backgroundColor: "#d8d8fdbd" }} height={495} />
            ) : !isFetchPeakVerificationTimeAnalysis && totalVerificationPerHour?.length === 0 ? (
                <Box sx={{
                    height: 495, textAlign: "center", flexDirection: "column",
                    justifyContent: "center", display: "flex", alignItems: "center",
                }}>
                    <img alt="empty-data-icon" src={EmptyIcon} width="110" style={{ marginTop: "-84px" }} />
                    <Typography sx={{ color: "#58565f", fontSize: 14, mt: 1 }}>
                        No data available for the selected date range
                    </Typography>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                        data={formattedVerificationPerHour}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar
                            dataKey="count"
                            shape={({ x, y, width, height, index }) => (
                                <Rectangle
                                    x={x}
                                    y={y}
                                    width={width}
                                    height={height}
                                    fill={formattedVerificationPerHour[index]?.color || "#5656d3"} // Use the mapped color for the count
                                />
                            )}
                        />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </StyledPaper>
    );
};

export default PeakVerificationTimeAnalysis;





// import React, { useEffect, useState } from "react";
// import {Skeleton, Box, Grid, Typography} from "@mui/material";
// import {BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from "recharts";
// import StyledPaper from "../common/styled-paper.component";
// import DateRangePickerComponent from "../common/CustomDateRangePicker";
// import moment from "moment";
// import useDashboard from '../../hooks/useDashboard';
// import EmptyIcon from "../../assets/images/empty-box.png";

// const PeakVerificationTimeAnalysis = (props) => {

//     const {totalVerificationPerHour, isFetchPeakVerificationTimeAnalysis, getTotalVerificationPerHour} = useDashboard();
//     const [date_range, setDateRange] = useState([moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')]);
//     const [formatted_verification_per_hour, setFormattedVerificationPerHour] = useState(null);

//     useEffect(()=>{
//         if(date_range == null) return;
//         getTotalVerificationPerHour(date_range[0], date_range[1]);
//     }, [date_range]);

//     useEffect(()=>{
//         if(totalVerificationPerHour == null) return;
//         var formatted_data = [];
//         for(var i=0; i < totalVerificationPerHour.length; i++){
//             var verification_obj = totalVerificationPerHour[i];
//             var verification_count = verification_obj.count;
//             var verification_hour = verification_obj.hour;
//             var name;
//             if(verification_hour < 10){
//                 name = "0" + i + ":00";
//             } else if(verification_hour == 0){
//                 name = "00:00";
//             } else {
//                 name = "00:" + verification_hour;
//             }
//             formatted_data[i] = {
//                 name: name, 
//                 count: verification_count
//             }
//         }
//         console.log("formatted_data here: ", formatted_data)
//         setFormattedVerificationPerHour(formatted_data);
//     }, [totalVerificationPerHour]);

//     const handleDateRangeChange = (startDate, endDate) => {
//         console.log(`Selected Date Range: ${startDate} to ${endDate}`);
//         setDateRange([startDate, endDate])
//     }

//     console.log("date_range: ", date_range)
//     console.log("totalVerificationPerHour a: ", totalVerificationPerHour)

//     return (
//         <StyledPaper>
            
//             <Grid container sx={{pb: 2}}>
//                 <Grid item xs={12} sm={6} lg={7.5}>
//                     <Typography>Peak Verification Time Analysis</Typography>
//                 </Grid>
//                 <Grid item xs={12} sm={6} lg={4.5}>
//                     <DateRangePickerComponent 
//                         initialStartDate={date_range[0]}
//                         initialEndDate={date_range[1]}
//                         ranges={{
//                             "Last 7 Days": [moment().subtract(6, 'days'), moment()],
//                             "This Month": [moment().startOf('month'), moment().endOf('month')],
//                             "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
//                             "Last 90 Days": [moment().subtract(89, 'days'), moment()],
//                         }}
//                         onDateRangeChange={handleDateRangeChange}
//                     />
//                 </Grid>
//             </Grid>

//             {isFetchPeakVerificationTimeAnalysis ?
//                 <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={495} />
//             :
//                 (!isFetchPeakVerificationTimeAnalysis && totalVerificationPerHour?.length === 0) ?
//                     <Box sx={{height: 495, textAlign: "center", flexDirection: "column", 
//                         justifyContent: "center", display: "flex", alignItems: "center", 
//                     }}>
//                         <img alt="empty-data-icon" src={EmptyIcon} width="110" style={{marginTop: "-84px"}} />

//                         <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
//                             No data available for the selected date range
//                         </Typography>
//                     </Box>
//             :
//                 <ResponsiveContainer width="100%" height={500}>
//                     <BarChart 
//                         width={500} height={400} 
//                         data={formatted_verification_per_hour}
//                         margin={{top: 5, right: 30, left: 20, bottom: 5}}
//                     >
//                         <CartesianGrid strokeDasharray="3 3" />
//                         <XAxis dataKey="name" />
//                         <YAxis />
//                         <Tooltip />
//                         <Legend />
//                         <Bar 
//                             dataKey="count" 
//                             fill="#5656d3" 
//                             activeBar={<Rectangle fill="#5656d3" stroke="#5656d3" />} 
//                         />
//                     </BarChart>
//                 </ResponsiveContainer>
//             }
//         </StyledPaper>
//     )
// }

// export default PeakVerificationTimeAnalysis;